import React, { useState } from "react";
import CustomSelect from "../../globalcontrols/customselect/customselect";
import BrandBreakdown from "./BrandBreakdown";
import HourlyWeekdayHeatmap from "./HourlyWeekdayHeatmap";
import MonthlySalesVariability from "./MonthlySalesVariability";
import KakaoCategoryPie from "./KakaoCategoryPie";
import ProductHourlyWeekdayHeatmap from "./ProductHourlyWeekdayHeatmap";
import ProductMonthlySalesVariability from "./ProductMonthlySalesVariability";
import DateRangePicker from "../../globalcontrols/daterangepicker/DateRangePicker";
import "./css/salessummary.css";

const money = (value) => `$${Number(value || 0).toLocaleString("es-CL", { maximumFractionDigits: 0 })}`;

const percentageChange = (current, previous) => {
  const currentValue = Number(current || 0);
  const previousValue = Number(previous || 0);
  if (previousValue === 0) return currentValue === 0 ? 0 : null;
  return ((currentValue - previousValue) / previousValue) * 100;
};

function Comparison({ current, previous, label }) {
  const change = percentageChange(current, previous);
  const positive = change !== null && change >= 0;
  return (
    <span className={`metric-comparison ${positive ? "positive" : "negative"}`}>
      {change === null ? "Nuevo" : `${positive ? "↑" : "↓"} ${Math.abs(change).toFixed(0)}%`} {label}
    </span>
  );
}

function SalesSummaryControl({
  role,
  showFilters,
  locations,
  selectedLocation,
  setSelectedLocation,
  users,
  selectedUser,
  setSelectedUser,
  summary,
  comparisons,
  loading,
  error,
  brandData,
  brandLoading,
  brandError,
  selectedSource,
  setSelectedSource,
  dateRange,
  setDateRange,
  hourlyData,
  hourlyLoading,
  hourlyError,
  hourlyLocation,
  setHourlyLocation,
  monthlyData,
  monthlyLoading,
  monthlyError,
  monthlyLocation,
  setMonthlyLocation,
  categoryData,
  categoryLoading,
  categoryError,
  categoryLocation,
  setCategoryLocation,
  productHourlyData,
  productHourlyLoading,
  productHourlyError,
  productHourlyLocation,
  setProductHourlyLocation,
  dashboardProducts,
  selectedDashboardProduct,
  setSelectedDashboardProduct,
  productMonthlyData,
  productMonthlyLoading,
  productMonthlyError,
}) {
  const [analyticsTab, setAnalyticsTab] = useState("hourly");
  if (!["superadmin", "admin", "cashier"].includes(role)) return null;

  const sourceOptions = [
    { value: "all", label: "Todos los negocios" },
    { value: "kakao", label: "KAKAO" },
    { value: "tablee", label: "Tablee" },
    { value: "oliva", label: "Oliva y Miel" },
  ];
  const selectedBrand = brandData?.brands?.find((brand) => brand.source === selectedSource);
  const selectedBrandTotals = selectedSource === "all" ? brandData?.totals : selectedBrand;
  const effectiveSummary = showFilters && selectedSource !== "kakao"
    ? {
      total_sales: selectedBrandTotals?.transaction_count || 0,
      total_amount: selectedBrandTotals?.collected_revenue || 0,
      average_ticket: selectedBrandTotals?.transaction_count
        ? Number(selectedBrandTotals.collected_revenue || 0) / Number(selectedBrandTotals.transaction_count)
        : 0,
    }
    : summary;
  const effectiveLoading = loading || (showFilters && selectedSource !== "kakao" && brandLoading);
  const metrics = [
    { label: "Ventas", value: effectiveSummary?.total_sales || 0, field: "total_sales", icon: "receipt_long" },
    { label: "Ingresos cobrados", value: money(effectiveSummary?.total_amount), field: "total_amount", icon: "payments" },
    { label: "Ticket promedio", value: money(effectiveSummary?.average_ticket), field: "average_ticket", icon: "trending_up" },
  ];

  return (
    <section className="dashboard-card sales-overview" aria-labelledby="sales-overview-title">
      <div className="card-heading">
        <div>
          <p className="card-kicker">Ventas</p>
          <h2 id="sales-overview-title">Resumen diario</h2>
        </div>
        <DateRangePicker
          iconOnly
          value={dateRange}
          onChange={setDateRange}
          firstAvailableDate={brandData?.first_available_date}
        />
      </div>

      {showFilters && (
        <div className="dashboard-filters" aria-label="Filtros del resumen">
          <CustomSelect
            label="Negocio"
            options={sourceOptions}
            selectedValue={selectedSource}
            onChange={setSelectedSource}
            labelExtractor={(item) => item.label}
            valueExtractor={(item) => item.value}
          />
          {selectedSource === "kakao" && (
            <>
              <CustomSelect
                label="Ubicación"
                options={locations}
                selectedValue={selectedLocation}
                onChange={setSelectedLocation}
                labelExtractor={(item) => item.name}
                valueExtractor={(item) => item.id.toString()}
              />
              <CustomSelect
                label="Usuario"
                options={users}
                selectedValue={selectedUser}
                onChange={setSelectedUser}
                labelExtractor={(item) => item.name}
                valueExtractor={(item) => item.username}
              />
            </>
          )}
        </div>
      )}

      {error && <div className="dashboard-alert" role="alert">{error}</div>}
      {effectiveLoading ? (
        <div className="dashboard-loading" role="status">Actualizando ventas…</div>
      ) : (
        <>
          <div className="metrics-grid">
            {metrics.map((metric) => (
              <article className="metric-card" key={metric.field}>
                <span className="material-symbols-outlined metric-icon" aria-hidden="true">{metric.icon}</span>
                <span className="metric-label">{metric.label}</span>
                <strong>{metric.value}</strong>
                {comparisons && selectedSource === "kakao" && (
                  <Comparison
                    current={effectiveSummary?.[metric.field]}
                    previous={comparisons.previousWeek?.[metric.field]}
                    label="vs. semana ant."
                  />
                )}
              </article>
            ))}
          </div>

          {!showFilters || selectedSource === "kakao" ? (
            <div className="payment-breakdown">
              <div><span>Efectivo</span><strong>{money(summary?.total_cash)}</strong></div>
              <div><span>Tarjeta</span><strong>{money(summary?.total_card)}</strong></div>
              <div><span>Saldo trabajador</span><strong>{money(summary?.total_worker)}</strong></div>
            </div>
          ) : (
            <div className="payment-breakdown">
              <div><span>Ingresos cobrados</span><strong>{money(selectedBrandTotals?.collected_revenue)}</strong></div>
              <div><span>Ingresos pendientes</span><strong>{money(selectedBrandTotals?.pending_revenue)}</strong></div>
              <div><span>Operaciones pagadas</span><strong>{Number(selectedBrandTotals?.transaction_count || 0).toLocaleString("es-CL")}</strong></div>
            </div>
          )}
          {showFilters && (
            <BrandBreakdown
              embedded
              data={brandData}
              loading={brandLoading}
              error={brandError}
              selectedSource={selectedSource}
              setSelectedSource={setSelectedSource}
              showFilter={false}
            />
          )}
          {showFilters && (
            <div className="sales-analytics">
              <div className="sales-analytics-tabs" role="tablist" aria-label="Gráficos de ventas">
                <button
                  type="button"
                  role="tab"
                  aria-selected={analyticsTab === "hourly"}
                  className={analyticsTab === "hourly" ? "active" : ""}
                  onClick={() => setAnalyticsTab("hourly")}
                >
                  Por hora y día
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={analyticsTab === "monthly"}
                  className={analyticsTab === "monthly" ? "active" : ""}
                  onClick={() => setAnalyticsTab("monthly")}
                >
                  Promedio mensual
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={analyticsTab === "categories"}
                  className={analyticsTab === "categories" ? "active" : ""}
                  onClick={() => setAnalyticsTab("categories")}
                >
                  Categorías KAKAO
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={analyticsTab === "product-hourly"}
                  className={analyticsTab === "product-hourly" ? "active" : ""}
                  onClick={() => setAnalyticsTab("product-hourly")}
                >
                  Producto por hora
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={analyticsTab === "product-monthly"}
                  className={analyticsTab === "product-monthly" ? "active" : ""}
                  onClick={() => setAnalyticsTab("product-monthly")}
                >
                  Promedio por producto
                </button>
              </div>
              <div className="sales-analytics-panel" role="tabpanel">
                <div className="sales-analytics-view" key={analyticsTab}>
                  {analyticsTab === "hourly" && (
                    <HourlyWeekdayHeatmap
                      embedded
                      data={hourlyData}
                      loading={hourlyLoading}
                      error={hourlyError}
                      locations={locations}
                      selectedLocation={hourlyLocation}
                      setSelectedLocation={setHourlyLocation}
                    />
                  )}
                  {analyticsTab === "monthly" && (
                    <MonthlySalesVariability
                      embedded
                      data={monthlyData}
                      loading={monthlyLoading}
                      error={monthlyError}
                      locations={locations}
                      selectedLocation={monthlyLocation}
                      setSelectedLocation={setMonthlyLocation}
                    />
                  )}
                  {analyticsTab === "categories" && (
                    <KakaoCategoryPie
                      data={categoryData}
                      loading={categoryLoading}
                      error={categoryError}
                      locations={locations}
                      selectedLocation={categoryLocation}
                      setSelectedLocation={setCategoryLocation}
                    />
                  )}
                  {analyticsTab === "product-hourly" && (
                    <ProductHourlyWeekdayHeatmap
                      data={productHourlyData}
                      loading={productHourlyLoading}
                      error={productHourlyError}
                      locations={locations}
                      selectedLocation={productHourlyLocation}
                      setSelectedLocation={setProductHourlyLocation}
                      products={dashboardProducts}
                      selectedProduct={selectedDashboardProduct}
                      setSelectedProduct={setSelectedDashboardProduct}
                    />
                  )}
                  {analyticsTab === "product-monthly" && (
                    <ProductMonthlySalesVariability
                      data={productMonthlyData}
                      loading={productMonthlyLoading}
                      error={productMonthlyError}
                      locations={locations}
                      selectedLocation={productHourlyLocation}
                      setSelectedLocation={setProductHourlyLocation}
                      products={dashboardProducts}
                      selectedProduct={selectedDashboardProduct}
                      setSelectedProduct={setSelectedDashboardProduct}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

export default SalesSummaryControl;
